package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/compose/room")
@Composable
fun RoomDatabasePage() {
    BlogLayout(
        PageProperties(
            "Room Database",
            ""),
        BlogProperties("Jetpack Compose", "Room Database")
    ) {
        Column(horizontalAlignment = Alignment.Start)
        {
            SpanText(
                text = "Auto Migration",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Database(
                        entities = [User::class], version = 4, exportSchema = true, autoMigrations = [
                            AutoMigration(
                                from = 1, to = 2,
                            ),
                            AutoMigration(
                                from = 2, to = 3, spec = MainDatabase.RenameAgeToCurrentAge::class
                            ),
                            AutoMigration(
                                from = 3, to = 4, spec = MainDatabase.DeleteAgeColumn::class
                            )
                        ]
                    )
                    abstract class MainDatabase : RoomDatabase() {

                        companion object {
                            fun getInstance(context: Context): MainDatabase {
                                return Room.databaseBuilder(context, MainDatabase::class.java, "db").build()
                            }
                        }

                        abstract fun getMainDao(): MainDao

                        @RenameColumn(tableName = "User", fromColumnName = "age", toColumnName = "current_age")
                        class RenameAgeToCurrentAge:AutoMigrationSpec

                        @DeleteColumn(tableName = "User", columnName = "current_age")
                        class DeleteAgeColumn : AutoMigrationSpec

                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Custom Migration",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     val MIGRATION_1_2 = object: Migration(1,2){
                         override fun migrate(database: SupportSQLiteDatabase) {
                             database.execSQL(
                                 "ALTER TABLE User ADD COLUMN age TEXT NOT NULL DEFAULT ''"
                             )
                         }
                     }

                     val MIGRATION_2_3 = object: Migration(2,3){
                         override fun migrate(database: SupportSQLiteDatabase) {
                             database.execSQL(
                                 "ALTER TABLE User RENAME COLUMN age TO current_age"
                             )
                         }
                     }

                     --

                     @Database(entities = [User::class], version = 3, exportSchema = true)
                     abstract class MainDatabase : RoomDatabase() {

                         companion object {
                             fun getInstance(context: Context): MainDatabase {
                                 return Room.databaseBuilder(context, MainDatabase::class.java, "db")
                                     .addMigrations(
                                         MIGRATION_1_2,MIGRATION_2_3
                                     )
                                     .build()
                             }
                         }

                         abstract fun getMainDao():MainDao

                     }

                     -- 
                     @Entity
                     data class User(
                         @PrimaryKey(autoGenerate = true)
                         val id: Int,
                         val name: String,
                         @ColumnInfo(name="age", defaultValue = "")
                         val age:String
                     )
                """.trimIndent()
            )

            SpanText(
                text = "Fallback to Destructive Migration",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val db = Room.databaseBuilder(
                        context,
                        AppDatabase::class.java,
                        "your_database"
                    ).fallbackToDestructiveMigration().build()
                    
                    @Entity
                    data class User(
                        @PrimaryKey(autoGenerate = true)
                        val id: Int,
                        val name: String,
                        @ColumnInfo(name="age", defaultValue = "")
                        val age:String
                    )
                """.trimIndent()
            )

            SpanText(
                text = "Query",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                       //Room database query (join, group by, max)
                       
                        @Dao
                        fun interface MasterDao {

                        //    @Query(
                        //        "WITH A as (select c.*, o.timestamp as wear, l.last_laundry, case when o.timestamp < l.last_laundry then 0 else 1 end as worn_count from clothing as c left join outfit as o on c.id=o.clothing_id left join (SELECT clothing_id, max(timestamp) as last_laundry from laundry group by clothing_id) as l on l.clothing_id=o.clothing_id) \n" +
                        //                "select A.id, A.image,  sum(A.worn_count) as worn_count from A group by A.id"
                        //    )
                            @Query("SELECT i.*, COUNT(o.id) AS worn_count\n" +
                                    "    FROM Clothing AS i\n" +
                                    "    LEFT JOIN outfit AS o ON i.id = o.clothing_id\n" +
                                    "    LEFT JOIN (\n" +
                                    "      SELECT id, MAX(timestamp) AS last_laundry_timestamp\n" +
                                    "      FROM laundry\n" +
                                    "      GROUP BY clothing_id\n" +
                                    "    ) AS l ON i.id = l.id\n" +
                                    "    WHERE o.timestamp > l.last_laundry_timestamp OR l.last_laundry_timestamp IS NULL\n" +
                                    "    GROUP BY i.id")
                            fun get(): Flow<List<WornCount>>
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //data/mapper/NoteMapper.kt

                        /**
                         * This is an extension function written on behave of [Note] class. And
                         * This function basically converted [Note] object to [NoteModel] object.
                         */
                        fun Note.toNoteModel(): NoteModel {
                            return NoteModel(id, title, content, lastEditTime)
                        }

                        /**
                         * This is an extension function written on behave of [NoteModel] class.
                         * This function basically converted [NoteModel] object to [Note] object.
                         */
                        fun NoteModel.toNote(): Note {
                            return Note(id, title, content, lastEditTime)
                        }

                        /**
                         * This extension function is written on behave of [NoteModel] class. This
                         * function will convert the [NoteModel] object into an object of [NoteAsListItem]
                         * class.
                         */
                        fun NoteModel.toNoteAsListItem(): NoteAsListItem {
                            return NoteAsListItem(
                                title = title,
                                id = id
                            )
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //data/model/NoteModel.kt
                        
                        import androidx.room.ColumnInfo
                        import androidx.room.Entity
                        import androidx.room.PrimaryKey

                        /**
                         * The class definition that will be used by room database to save a note.
                         */
                        @Entity(tableName = "note_table")
                        data class NoteModel(

                            /**
                             * We will preserve every [NoteModel] in room database with an unique id.
                             * So that we can perform queries on different [NoteModel] specifically.
                             * Also we won't have to worry about unique ID generation cause it will
                             * be done by room itself.
                             */
                            @ColumnInfo(name = "id")
                            @PrimaryKey(autoGenerate = true)
                            val id: Long = 0,

                            /**
                             * This attribute will hold the title of the [NoteModel]. In data layer
                             * we are not putting any specific length conditions for the title. User
                             * can have 2 [NoteModel] with same [title].
                             */
                            @ColumnInfo(name = "title")
                            val title: String,

                            /**
                             * This attribute will hold the content or body part of the [NoteModel].
                             * Here also - no length limitation.
                             */
                            @ColumnInfo(name = "content")
                            val content: String,

                            /**
                             * We need to store this info otherwise we won't be able to short the
                             * note list according to last edit time. Every time when a new note will
                             * be added in database then we have to add that particular [NoteModel]
                             * with current time as the [lastEditTime]. On the other hand when a new
                             * note item will be modified or updated then we also have to update the
                             * [lastEditTime] with the current time at that particular time.
                             */
                            @ColumnInfo(name = "last_edit_time")
                            val lastEditTime: Long
                        )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         //
                         interface NoteRepository {

                             /**
                              * Get all the notes from database.
                              *
                              * @return A flow of the list of notes
                              */
                             fun getAllNotes(): Flow<List<NoteModel>>

                             /**
                              * Get only one note from the database.
                              *
                              * @param noteId the id of the note that you are searching for.
                              * @return A flow of a single note object.
                              */
                             fun getSingleNote(noteId: Long): Flow<NoteModel?>

                             /**
                              * Add new note or update previous one with the same API. When
                              * we will add a new note then we will pass 0 as noteId and when
                              * we will update a previous note then we will pass the previous
                              * id as noteId. Room database will automatically REPLACE if the
                              * id is duplicated.
                              *
                              * @param note the note object that we want to add or update.
                              * @return the note id that got added or updated.
                              */
                             suspend fun addOrUpdateNote(note: NoteModel): Long

                             /**
                              * Delete a particular note by using the noteId.
                              *
                              * @param noteId the note id that you want to delete.
                              * @return the row count that got affected by the delete command.
                              */
                             suspend fun deleteNote(noteId: Long): Int
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        class NoteRepositoryImpl(private val noteDao: NoteDao) : NoteRepository {
                            override fun getAllNotes(): Flow<List<NoteModel>> {
                                return noteDao.getAllNotes()
                            }

                            override fun getSingleNote(noteId: Long): Flow<NoteModel?> {
                                return noteDao.getSingleNote(noteId)
                            }

                            override suspend fun addOrUpdateNote(note: NoteModel): Long {
                                return noteDao.addOrUpdateNote(note)
                            }

                            override suspend fun deleteNote(noteId: Long): Int {
                                return noteDao.deleteNote(noteId)
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @Dao
                        interface NoteDao {
                            @Query("SELECT * from note_table")
                            fun getAllNotes(): Flow<List<NoteModel>>

                            @Query("SELECT * from note_table WHERE id = :noteId")
                            fun getSingleNote(noteId: Long): Flow<NoteModel?>

                            @Insert(onConflict = OnConflictStrategy.REPLACE)
                            suspend fun addOrUpdateNote(note: NoteModel): Long

                            @Query("DELETE from note_table WHERE id = :noteId")
                            suspend fun deleteNote(noteId: Long): Int
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         import androidx.room.Database
                         import androidx.room.RoomDatabase

                         @Database(
                             entities = [NoteModel::class],
                             version = 1,
                             exportSchema = false
                         )
                         abstract class NoteDatabase: RoomDatabase() {
                             abstract fun noteDao(): NoteDao

                             companion object {
                                 const val DB_NAME = "note_db"
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //di/AppModule.kt
                        @Module
                        @InstallIn(SingletonComponent::class)
                        class AppModule {

                            @Provides
                            @Singleton
                            fun provideNoteDatabase(@ApplicationContext appContext: Context): NoteDatabase {
                                return Room.databaseBuilder(
                                    context = appContext,
                                    klass = NoteDatabase::class.java,
                                    name = NoteDatabase.DB_NAME
                                ).build()
                            }

                            @Provides
                            @Singleton
                            fun provideNoteDao(noteDatabase: NoteDatabase): NoteDao {
                                return noteDatabase.noteDao()
                            }

                            @Provides
                            @Singleton
                            fun provideNoteRepository(noteDao: NoteDao): NoteRepository {
                                return NoteRepositoryImpl(noteDao)
                            }

                            @Provides
                            fun provideGetNoteListUseCase(repository: NoteRepository): GetNotesUseCase {
                                return GetNotesUseCase(repository)
                            }

                            @Provides
                            fun provideSingleNoteUseCase(repository: NoteRepository): GetSingleNoteUseCase {
                                return GetSingleNoteUseCase(repository)
                            }

                            @Provides
                            fun provideAddNoteUseCase(repository: NoteRepository): AddNoteUseCase {
                                return AddNoteUseCase(repository)
                            }

                            @Provides
                            fun providesDeleteNoteUseCase(repository: NoteRepository): DeleteNoteUseCase {
                                return DeleteNoteUseCase(repository)
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        data class Note(
                            val id: Long = 0,
                            val title: String,
                            val content: String,
                            val lastEditTime: Long
                        )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         /**
                          * You may have question like why this class is necessary cause we already
                          * have a Note class for this with more parameters. But this class is created
                          * with having mind that the title will have only one line. But when accessing
                          * the Note object it won't have one line title. Then to full fill requirement
                          * we have to modify the title into one line. So we want to perform this string
                          * modification inside the view model. And the view model then return or update
                          * the list of [NoteAsListItem] objects. We can use the existing Note class but
                          * that won't be good cause we have to parse single line title every time when
                          * loading the list.
                          */
                         data class NoteAsListItem(
                             /**
                              * We need this attribute cause we will show title in the list of notes.
                              */
                             val title: String,

                             /**
                              * We need this attribute to identify which Note item was selected by the
                              * user to perform specific operation.
                              */
                             val id: Long = 0
                         )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        /**
                         * Use this class to add new note in the database. Check [execute] API for
                         * more details.
                         */
                        class AddNoteUseCase(private val repository: NoteRepository) {

                            /**
                             * Checks whether the input note is ready to add in the database or not.
                             * If the note has missing information then this function will return an
                             * exception by mentioning that error. In general this function checks,
                             * is the note title empty or not, is the note content empty or not, and
                             * is the add operation successful or not.
                             *
                             * @param note the note you want to add in the database.
                             * @throws AddNoteException when missing information in the input note
                             */
                            @Throws(AddNoteException::class)
                            suspend fun execute(note: Note) {
                                if (note.title.trim().isEmpty()) {
                                    throw AddNoteException(R.string.error_empty_title)
                                }
                                if (note.content.trim().isEmpty()) {
                                    throw AddNoteException(R.string.error_empty_content)
                                }
                                if (repository.addOrUpdateNote(note.toNoteModel()) <= 0) {
                                    throw AddNoteException(R.string.error_unknown_exception)
                                }
                            }
                        }

                        /**
                         * This exception class will be used when user want to add a new note, but
                         * that note has missing required information or all the fields are not
                         * properly filled. Check [AddNoteUseCase] class for more details.
                         */
                        class AddNoteException(val reason: Int) : Exception()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        /**
                         * Use this class to delete a note from database. Check [execute] API
                         * for more details.
                         */
                        class DeleteNoteUseCase(private val repository: NoteRepository) {

                            /**
                             * Tries to delete note with the input noteId. Throws exception
                             * if the operation is not successful. Generally this function checks
                             * whether the database operation is successful or not. Even if you
                             * pass a noteId which is not present in the database then this function
                             * will throw exception.
                             *
                             * @param noteId The noteId that you want to delete from the database.
                             * @throws DeleteNoteException when the delete operation is not successful.
                             */
                            @Throws(DeleteNoteException::class)
                            suspend fun execute(noteId: Long) {
                                if (repository.deleteNote(noteId) <= 0) {
                                    throw DeleteNoteException(R.string.error_unknown_exception)
                                }
                            }
                        }

                        /**
                         * This exception class will be used by [DeleteNoteUseCase] class when
                         * the deletion operation will be failed. Check the delete API
                         * [DeleteNoteUseCase.execute] for more details.
                         */
                        class DeleteNoteException(val reason: Int) : Exception()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         /**
                          * To get all the notes use this class.
                          */
                         class GetNotesUseCase(private val repository: NoteRepository) {

                             /**
                              * This function will return all the notes inside a list. All the notes
                              * will be inserted into the list by descending order according to their
                              * lastEditTime.
                              */
                             fun execute(): Flow<List<NoteAsListItem>> {
                                 return repository.getAllNotes().map { notes ->
                                     notes.sortedByDescending { it.lastEditTime }
                                 }.map { noteModelList -> noteModelList.map { it.toNoteAsListItem() } }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        /**
                         * Use this class to get a single note by using it's noteId. Check [execute]
                         * API for more details.
                         */
                        class GetSingleNoteUseCase(private val repository: NoteRepository) {

                            /**
                             * This function returns the corresponding note object inside a flow object.
                             *
                             * @param noteId The noteId of the note object that you want in return.
                             * @return The corresponding note object with a Flow wrapper
                             */
                            fun execute(noteId: Long): Flow<Note?> {
                                return repository.getSingleNote(noteId).map { it?.toNote() }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        private const val ANIMATION_OFFSET = 500
                        private const val ANIMATION_DURATION = 500

                        @Composable
                        fun AppNavigation() {

                            val navController = rememberNavController()

                            NavHost(
                                modifier = Modifier.background(MaterialTheme.colorScheme.surface),
                                navController = navController,
                                startDestination = AppScreen.NoteListScreen.router
                            ) {
                                composable(
                                    route = AppScreen.NoteListScreen.router,
                                    exitTransition = {
                                        slideOutHorizontally(
                                            targetOffsetX = { -ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeOut(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    },
                                    popEnterTransition = {
                                        slideInHorizontally(
                                            initialOffsetX = { -ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeIn(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    }
                                ) {
                                    NoteListScreen(navController = navController)
                                }

                                composable(
                                    route = AppScreen.AddOrEditNoteScreen.router + "?noteId={noteId}",
                                    arguments = listOf(navArgument("noteId") { type = NavType.LongType }),
                                    enterTransition = {
                                        slideInHorizontally(
                                            initialOffsetX = { ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeIn(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    },
                                    popExitTransition = {
                                        slideOutHorizontally(
                                            targetOffsetX = { ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeOut(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    }
                                ) { AddEditNoteScreen(navController = navController) }

                                composable(
                                    route = AppScreen.ShowNoteScreen.router + "?noteId={noteId}",
                                    arguments = listOf(navArgument("noteId") { type = NavType.LongType }),
                                    enterTransition = {
                                        slideInHorizontally(
                                            initialOffsetX = { ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeIn(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    },
                                    exitTransition = {
                                        slideOutHorizontally(
                                            targetOffsetX = { -ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeOut(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    },
                                    popEnterTransition = {
                                        slideInHorizontally(
                                            initialOffsetX = { -ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeIn(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    },
                                    popExitTransition = {
                                        slideOutHorizontally(
                                            targetOffsetX = { ANIMATION_OFFSET },
                                            animationSpec = tween(durationMillis = ANIMATION_DURATION)
                                        ) + fadeOut(animationSpec = tween(durationMillis = ANIMATION_DURATION))
                                    }
                                ) { ShowNoteScreen(navController = navController) }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         sealed class AppScreen(val router: String) {
                             data object NoteListScreen : AppScreen("note_list_screen")
                             data object AddOrEditNoteScreen : AppScreen("add_edit_note_screen")
                             data object ShowNoteScreen : AppScreen("show_note_screen")
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @OptIn(ExperimentalMaterial3Api::class)
                         @Composable
                         fun AddEditNoteScreen(
                             navController: NavController,
                             viewModel: AddEditNoteViewModel = hiltViewModel()
                         ) {

                             val context = LocalContext.current
                             val titleTextState = viewModel.titleText.collectAsState()
                             val contentTextState = viewModel.contentText.collectAsState()
                             val isEditingNoteState = viewModel.isEditingNote.collectAsState()
                             val verticalScrollState = rememberScrollState()

                             LaunchedEffect(Unit) {
                                 viewModel.toastMessage.collect {
                                     it?.let { resourceId ->
                                         val actualMessage = context.getString(resourceId)
                                         Toast.makeText(context, actualMessage, Toast.LENGTH_SHORT).show()
                                     }
                                 }
                             }

                             LaunchedEffect(Unit) {
                                 viewModel.shouldCloseScreen.collect { shouldClose ->
                                     if (shouldClose == true) {
                                         navController.navigateUp()
                                     }
                                 }
                             }

                             Scaffold(
                                 topBar = {
                                     TopAppBar(
                                         colors = TopAppBarDefaults.topAppBarColors(
                                             containerColor = MaterialTheme.colorScheme.background,
                                             titleContentColor = MaterialTheme.colorScheme.onBackground
                                         ),
                                         title = {
                                             isEditingNoteState.value?.let { isEdit ->
                                                 Text(text = if (isEdit) "Edit" else "Add")
                                             }
                                         },
                                         actions = {
                                             IconButton(onClick = { viewModel.handleEvent(AddEditUiEvent.SaveNote) }) {
                                                 Icon(imageVector = Icons.Default.Done, contentDescription = "Add")
                                             }
                                         },
                                         navigationIcon = {
                                             IconButton(onClick = { navController.navigateUp() }) {
                                                 Icon(imageVector = Icons.Filled.ArrowBack, contentDescription = "Back")
                                             }
                                         }
                                     )
                                 }
                             ) { innerPadding ->
                                 Column(
                                     modifier = Modifier
                                         .background(MaterialTheme.colorScheme.surface)
                                         .fillMaxSize()
                                         .imePadding()
                                         .verticalScroll(state = verticalScrollState)
                                         .padding(innerPadding),
                                 ) {
                                     TitleInputView(text = titleTextState.value) {
                                         viewModel.handleEvent(AddEditUiEvent.UpdateTitle(it))
                                     }
                                     MenuDivider(paddingStart = 16, paddingEnd = 16)
                                     ContentInputView(text = contentTextState.value) {
                                         viewModel.handleEvent(AddEditUiEvent.UpdateContent(it))
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         
                         @HiltViewModel
                         class AddEditNoteViewModel @Inject constructor(
                             getSingleNoteUseCase: GetSingleNoteUseCase,
                             private val addNoteUseCase: AddNoteUseCase,
                             private val savedState: SavedStateHandle
                         ) : ViewModel() {

                             private val _titleText = MutableStateFlow("")

                             /**
                              * This attribute will be used to track the input for titleText. User
                              * may enter some character and then there could be screen rotation,
                              * so we can't let the data vanished.
                              */
                             val titleText: StateFlow<String> = _titleText

                             private val _contentText = MutableStateFlow("")

                             /**
                              * This attribute will be used to track content text same as previous
                              * attribute [titleText]
                              */
                             val contentText: StateFlow<String> = _contentText

                             private val _toastMessage = MutableSharedFlow<Int?>()

                             /**
                              * This attribute will be used to pass events to UI layer when there
                              * will be needed to show a toast message. Directly the value of the
                              * string reference will be passed as integer.
                              */
                             val toastMessage: SharedFlow<Int?> = _toastMessage

                             private val _shouldCloseScreen = MutableSharedFlow<Boolean?>()

                             /**
                              * This attribute represent that current screen should be closed.
                              * Generally this will be happen when the add or update operation will
                              * be successful.
                              */
                             val shouldCloseScreen: SharedFlow<Boolean?> = _shouldCloseScreen

                             /**
                              * This attribute will hold the currentNoteId that we are adding or
                              * editing, when adding new note this attribute will hold -1, and when
                              * we are editing a note then this attribute will hold the noteId value
                              * of that corresponding note. It will be initialized by [savedState],
                              * as soon as the view model will be created.
                              */
                             private var relatedNoteId: Long = -1L

                             private val _isEditingNote = MutableStateFlow<Boolean?>(null)

                             /**
                              * We are directly exposing this attribute to UI layer so that it may
                              * have the data to define whether we are adding a new note or we are
                              * just editing a previous one. By depending on this value UI layer may
                              * have to change it's title.
                              */
                             val isEditingNote: StateFlow<Boolean?> = _isEditingNote

                             init {
                                 viewModelScope.launch {
                                     relatedNoteId = savedState.get<Long>("noteId") ?: -1L
                                     _isEditingNote.emit(relatedNoteId != -1L)
                                     if (relatedNoteId == -1L) return@launch // -1 for add note screen
                                     getSingleNoteUseCase.execute(relatedNoteId).firstOrNull()?.let {
                                         _titleText.emit(it.title)
                                         _contentText.emit(it.content)
                                     }
                                 }
                             }

                             fun handleEvent(event: AddEditUiEvent) = viewModelScope.launch {
                                 when (event) {
                                     is AddEditUiEvent.UpdateTitle -> {
                                         _titleText.tryEmit(event.title)
                                     }

                                     is AddEditUiEvent.UpdateContent -> {
                                         _contentText.tryEmit(event.content)
                                     }

                                     AddEditUiEvent.SaveNote -> {
                                         try {
                                             val currentNote = Note(
                                                 title = titleText.value,
                                                 content = contentText.value,
                                                 lastEditTime = System.currentTimeMillis(),
                                                 id = if (relatedNoteId == -1L) 0L else relatedNoteId
                                             )
                                             addNoteUseCase.execute(currentNote)
                                             _shouldCloseScreen.emit(true)
                                         } catch (exception: AddNoteException) {
                                             _toastMessage.emit(exception.reason)
                                         }
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         sealed class AddEditUiEvent {
                             class UpdateTitle(val title: String) : AddEditUiEvent()
                             class UpdateContent(val content: String) : AddEditUiEvent()
                             data object SaveNote : AddEditUiEvent()
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun ContentInputView(
                             text: String,
                             hintText: String = "Type something..",
                             onValueChanged: (String) -> Unit
                         ) {
                             Box(
                                 modifier = Modifier
                                     .fillMaxWidth()
                                     .background(color = MaterialTheme.colorScheme.surface)
                                     .padding(16.dp)
                             ) {
                                 BasicTextField(
                                     modifier = Modifier.fillMaxWidth(),
                                     value = text,
                                     onValueChange = onValueChanged,
                                     textStyle = TextStyle(
                                         color = MaterialTheme.colorScheme.onSurface,
                                         fontFamily = FontFamily.SansSerif,
                                         fontSize = 20.sp,
                                         fontStyle = FontStyle.Normal,
                                         fontWeight = FontWeight.Light
                                     ),
                                     cursorBrush = SolidColor(MaterialTheme.colorScheme.onSurface),
                                     singleLine = false,
                                 )
                                 if (text.isEmpty()) {
                                     Text(
                                         modifier = Modifier.fillMaxWidth(),
                                         text = hintText,
                                         color = MaterialTheme.colorScheme.onSurfaceVariant,
                                         fontFamily = FontFamily.SansSerif,
                                         fontSize = 20.sp,
                                         fontStyle = FontStyle.Normal,
                                         fontWeight = FontWeight.Light,
                                     )
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun TitleInputView(
                             text: String,
                             hintText: String = "Title..",
                             onValueChanged: (String) -> Unit
                         ) {
                             Box(
                                 modifier = Modifier
                                     .fillMaxWidth()
                                     .background(color = MaterialTheme.colorScheme.surface)
                                     .padding(16.dp)
                             ) {
                                 BasicTextField(
                                     modifier = Modifier.fillMaxWidth(),
                                     value = text,
                                     onValueChange = onValueChanged,
                                     textStyle = TextStyle(
                                         color = MaterialTheme.colorScheme.onSurface,
                                         fontFamily = FontFamily.SansSerif,
                                         fontSize = 24.sp,
                                         fontStyle = FontStyle.Normal,
                                         fontWeight = FontWeight.Light,
                                     ),
                                     cursorBrush = SolidColor(MaterialTheme.colorScheme.onSurface),
                                     singleLine = false,
                                 )
                                 if (text.isEmpty()) {
                                     Text(
                                         modifier = Modifier.fillMaxWidth(),
                                         text = hintText,
                                         color = MaterialTheme.colorScheme.onSurfaceVariant,
                                         fontFamily = FontFamily.SansSerif,
                                         fontSize = 24.sp,
                                         fontStyle = FontStyle.Normal,
                                         fontWeight = FontWeight.Light,
                                     )
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun MenuDivider(
                             height: Int = 1,
                             paddingStart: Int = 0,
                             paddingEnd: Int = 0
                         ) {
                             Divider(
                                 modifier = Modifier
                                     .fillMaxWidth()
                                     .height(height.dp)
                                     .padding(start = paddingStart.dp, end = paddingEnd.dp)
                             )
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun NoteListEmptyView(onTextClicked: () -> Unit) {
                             Box(
                                 modifier = Modifier.fillMaxSize(),
                                 contentAlignment = Alignment.Center,
                             ) {
                                 Text(
                                     modifier = Modifier.clickable {
                                         onTextClicked.invoke()
                                     },
                                     text = "Click to add new note",
                                     fontSize = 18.sp,
                                     fontFamily = FontFamily.SansSerif,
                                     fontWeight = FontWeight.Medium
                                 )
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun NoteListNonEmptyValue(
                             noteItems: List<NoteAsListItem>,
                             onItemClicked: (itemId: Long) -> Unit
                         ) {
                             LazyColumn {
                                 items(
                                     count = noteItems.size,
                                     key = { i -> i }
                                 ) {
                                     NoteViewAsListItem(
                                         noteAsListItem = noteItems[it],
                                         onItemClick = onItemClicked
                                     )
                                     MenuDivider()
                                 }
                             }
                         }

                         @Composable
                         private fun NoteViewAsListItem(
                             noteAsListItem: NoteAsListItem,
                             onItemClick: (itemId: Long) -> Unit
                         ) {
                             Text(
                                 modifier = Modifier
                                     .fillMaxWidth()
                                     .wrapContentHeight()
                                     .background(MaterialTheme.colorScheme.surface)
                                     .clickable { onItemClick(noteAsListItem.id) }
                                     .padding(16.dp),
                                 text = noteAsListItem.title,
                                 color = MaterialTheme.colorScheme.onSurface,
                                 fontFamily = FontFamily.SansSerif,
                                 fontWeight = FontWeight.Medium,
                                 fontSize = 22.sp
                             )
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @OptIn(ExperimentalMaterial3Api::class)
                         @Composable
                         fun NoteListScreen(
                             navController: NavController,
                             viewModel: NoteListViewModel = hiltViewModel()
                         ) {
                             val noteItems: State<List<NoteAsListItem>?> = viewModel.noteList.collectAsState()

                             fun openAddOrEditScreen(noteId: Long) {
                                 val route = AppScreen.AddOrEditNoteScreen.router
                                 val param = "noteId=@noteId"
                                 navController.navigate("@route?@param")
                             }

                             fun openSingleNoteScreen(noteId: Long) {
                                 val route = AppScreen.ShowNoteScreen.router
                                 val param = "noteId=@noteId"
                                 navController.navigate("@route?@param")
                             }

                             Scaffold(
                                 topBar = {
                                     TopAppBar(
                                         colors = topAppBarColors(
                                             containerColor = MaterialTheme.colorScheme.background,
                                             titleContentColor = MaterialTheme.colorScheme.onBackground
                                         ),
                                         title = { Text(text = "Notepad") },
                                         actions = {
                                             IconButton(onClick = { openAddOrEditScreen(-1) }) {
                                                 Icon(imageVector = Icons.Default.Add, contentDescription = "Add")
                                             }
                                         }
                                     )
                                 }
                             ) { innerPadding ->
                                 Surface(
                                     modifier = Modifier
                                         .fillMaxSize()
                                         .background(MaterialTheme.colorScheme.surface)
                                         .padding(innerPadding)
                                 ) {
                                     noteItems.value?.let { validItems ->
                                         when (validItems.isEmpty()) {
                                             true -> NoteListEmptyView {
                                                 openAddOrEditScreen(-1)
                                             }

                                             else -> NoteListNonEmptyValue(noteItems = validItems) {
                                                 openSingleNoteScreen(it)
                                             }
                                         }
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @HiltViewModel
                         class NoteListViewModel @Inject constructor(getNotesUseCase: GetNotesUseCase) : ViewModel() {

                             private val _noteList = MutableStateFlow<List<NoteAsListItem>?>(null)
                             val noteList: StateFlow<List<NoteAsListItem>?> = _noteList

                             init {
                                 viewModelScope.launch {
                                     getNotesUseCase.execute().collect {
                                         _noteList.emit(it)
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @Composable
                         fun DeleteNoteDialog(
                             onConfirm: () -> Unit,
                             onDismiss: () -> Unit
                         ) {
                             val openDialog = remember { mutableStateOf(true) }

                             if (openDialog.value) {
                                 AlertDialog(
                                     onDismissRequest = {
                                         openDialog.value = false
                                         onDismiss()
                                     },
                                     title = { Text(stringResource(id = R.string.delete_dialog_title)) },
                                     text = { Text(stringResource(id = R.string.delete_dialog_content)) },
                                     confirmButton = {
                                         Button(
                                             onClick = {
                                                 openDialog.value = false
                                                 onConfirm()
                                             }
                                         ) {
                                             Text(stringResource(id = R.string.delete_dialog_positive_button_text))
                                         }
                                     },
                                     dismissButton = {
                                         Button(
                                             onClick = {
                                                 openDialog.value = false
                                                 onDismiss()
                                             }
                                         ) {
                                             Text(stringResource(id = R.string.delete_dialog_negative_button_text))
                                         }
                                     }
                                 )
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @OptIn(ExperimentalMaterial3Api::class)
                         @Composable
                         fun ShowNoteScreen(
                             navController: NavController,
                             viewModel: ShowNoteViewModel = hiltViewModel()
                         ) {

                             val context = LocalContext.current
                             val currentNote = viewModel.currentNote.collectAsState()
                             val verticalScrollState = rememberScrollState()
                             val showDeleteDialog = remember { mutableStateOf(false) }

                             LaunchedEffect(Unit) {
                                 viewModel.toastMessage.collect {
                                     it?.let { resourceId ->
                                         val actualMessage = context.getString(resourceId)
                                         Toast.makeText(context, actualMessage, Toast.LENGTH_SHORT).show()
                                     }
                                 }
                             }

                             LaunchedEffect(Unit) {
                                 viewModel.shouldCloseScreen.collect { shouldClose ->
                                     if (shouldClose == true) {
                                         navController.navigateUp()
                                     }
                                 }
                             }

                             if (showDeleteDialog.value) {
                                 DeleteNoteDialog(
                                     onConfirm = {
                                         currentNote.value?.id?.let {
                                             viewModel.handleEvent(ShowNoteUiEvent.DeleteNote(it))
                                         }
                                         showDeleteDialog.value = false
                                     },
                                     onDismiss = {
                                         showDeleteDialog.value = false
                                     }
                                 )
                             }

                             fun openAddOrEditScreen(noteId: Long?) {
                                 noteId?.let {
                                     val route = AppScreen.AddOrEditNoteScreen.router
                                     val param = "noteId=@it"
                                     navController.navigate("@route?@param")
                                 }
                             }

                             Scaffold(
                                 topBar = {
                                     TopAppBar(
                                         colors = TopAppBarDefaults.topAppBarColors(
                                             containerColor = MaterialTheme.colorScheme.background,
                                             titleContentColor = MaterialTheme.colorScheme.onBackground
                                         ),
                                         title = { },
                                         actions = {
                                             IconButton(onClick = { showDeleteDialog.value = true }) {
                                                 Icon(imageVector = Icons.Default.Delete, contentDescription = "Edit")
                                             }
                                             IconButton(onClick = { openAddOrEditScreen(currentNote.value?.id) }) {
                                                 Icon(imageVector = Icons.Default.Edit, contentDescription = "Edit")
                                             }
                                         },
                                         navigationIcon = {
                                             IconButton(onClick = { navController.navigateUp() }) {
                                                 Icon(imageVector = Icons.Filled.ArrowBack, contentDescription = "Back")
                                             }
                                         }
                                     )
                                 }
                             ) { innerPadding ->
                                 Column(
                                     modifier = Modifier
                                         .background(MaterialTheme.colorScheme.surface)
                                         .fillMaxSize()
                                         .verticalScroll(state = verticalScrollState)
                                         .padding(innerPadding),
                                 ) {
                                     currentNote.value?.let {
                                         Text(
                                             modifier = Modifier
                                                 .fillMaxWidth()
                                                 .padding(16.dp),
                                             text = it.title,
                                             color = MaterialTheme.colorScheme.onSurfaceVariant,
                                             fontFamily = FontFamily.SansSerif,
                                             fontSize = 24.sp,
                                             fontStyle = FontStyle.Normal,
                                             fontWeight = FontWeight.Light,
                                         )
                                         MenuDivider(paddingStart = 16, paddingEnd = 16)
                                         Text(
                                             modifier = Modifier
                                                 .fillMaxWidth()
                                                 .padding(16.dp),
                                             text = it.content,
                                             color = MaterialTheme.colorScheme.onSurfaceVariant,
                                             fontFamily = FontFamily.SansSerif,
                                             fontSize = 20.sp,
                                             fontStyle = FontStyle.Normal,
                                             fontWeight = FontWeight.Light,
                                         )
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         sealed class ShowNoteUiEvent {
                             class DeleteNote(val noteId: Long) : ShowNoteUiEvent()
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @HiltViewModel
                         class ShowNoteViewModel @Inject constructor(
                             getSingleNoteUseCase: GetSingleNoteUseCase,
                             private val deleteNoteUseCase: DeleteNoteUseCase,
                             savedStateHandle: SavedStateHandle
                         ) : ViewModel() {

                             private val _currentNote = MutableStateFlow<Note?>(null)
                             val currentNote: StateFlow<Note?> = _currentNote

                             private val _shouldCloseScreen = MutableSharedFlow<Boolean?>()
                             val shouldCloseScreen: SharedFlow<Boolean?> = _shouldCloseScreen

                             private val _toastMessage = MutableSharedFlow<Int?>()
                             val toastMessage: SharedFlow<Int?> = _toastMessage

                             init {
                                 viewModelScope.launch {
                                     val noteId = savedStateHandle.get<Long>("noteId") ?: return@launch
                                     getSingleNoteUseCase.execute(noteId).collect { noteResult ->
                                         noteResult?.let { _currentNote.emit(it) }
                                     }
                                 }
                             }

                             fun handleEvent(uiEvent: ShowNoteUiEvent) = viewModelScope.launch {
                                 if (uiEvent is ShowNoteUiEvent.DeleteNote) {
                                     try {
                                         deleteNoteUseCase.execute(noteId = uiEvent.noteId)
                                         _shouldCloseScreen.emit(true)
                                     } catch (exception: DeleteNoteException) {
                                         _toastMessage.emit(exception.reason)
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         val lightBackgroundColor100 = Color(0xFF3B3B3B)
                         val lightBackgroundColor50 = Color(0x883B3B3B)
                         val darkBackgroundColor100 = Color(0xFF252525)
                         val darkBackgroundColor50 = Color(0x88252525)
                         val appTextColor100 = Color(0xFFFFFFFF)
                         val appTextColor50 = Color(0x88FFFFFF)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         private val SingleColorScheme = lightColorScheme(
                             background = lightBackgroundColor100,
                             onBackground = appTextColor100,
                             primary = lightBackgroundColor100,
                             onPrimary = appTextColor100,
                             surface = darkBackgroundColor100,
                             onSurface = appTextColor100,
                             onSurfaceVariant = appTextColor50,
                             outlineVariant = lightBackgroundColor50,
                         )

                         @Composable
                         fun NotepadTheme(content: @Composable () -> Unit) {
                             val view = LocalView.current
                             if (!view.isInEditMode) {
                                 SideEffect {
                                     val window = (view.context as Activity).window
                                     window.statusBarColor = SingleColorScheme.background.toArgb()
                                     window.navigationBarColor = SingleColorScheme.background.toArgb()
                                     WindowCompat.getInsetsController(window, view).isAppearanceLightStatusBars = false
                                 }
                             }

                             MaterialTheme(
                                 colorScheme = SingleColorScheme,
                                 typography = Typography,
                                 content = content
                             )
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         // Set of Material typography styles to start with
                         val Typography = Typography(
                             bodyLarge = TextStyle(
                                 fontFamily = FontFamily.Default,
                                 fontWeight = FontWeight.Normal,
                                 fontSize = 16.sp,
                                 lineHeight = 24.sp,
                                 letterSpacing = 0.5.sp
                             )
                             /* Other default text styles to override
                             titleLarge = TextStyle(
                                 fontFamily = FontFamily.Default,
                                 fontWeight = FontWeight.Normal,
                                 fontSize = 22.sp,
                                 lineHeight = 28.sp,
                                 letterSpacing = 0.sp
                             ),
                             labelSmall = TextStyle(
                                 fontFamily = FontFamily.Default,
                                 fontWeight = FontWeight.Medium,
                                 fontSize = 11.sp,
                                 lineHeight = 16.sp,
                                 letterSpacing = 0.5.sp
                             )
                             */
                         )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @AndroidEntryPoint
                         class MainActivity : ComponentActivity() {
                             override fun onCreate(savedInstanceState: Bundle?) {
                                 super.onCreate(savedInstanceState)
                                 setContent {
                                     NotepadTheme {
                                         Surface(
                                             modifier = Modifier
                                                 .fillMaxSize()
                                                 .background(MaterialTheme.colorScheme.background)
                                         ) {
                                             AppNavigation()
                                         }
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @HiltAndroidApp
                        class MainApplication : Application()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class NoteRepositoryImplTest {

                             private lateinit var repository: NoteRepository
                             private lateinit var noteDao: NoteDao

                             @Before
                             fun setup() {
                                 noteDao = mockk()
                                 repository = NoteRepositoryImpl(noteDao)
                             }

                             @Test
                             fun `testing getAllNotes when the list is empty`() = runBlocking {
                                 coEvery { noteDao.getAllNotes() } returns flowOf(emptyList())

                                 val result = repository.getAllNotes().first()

                                 coVerify(exactly = 1) { noteDao.getAllNotes() }
                                 assert(result.isEmpty())
                             }

                             @Test
                             fun `testing getAllNotes when the list has an item`() = runBlocking {
                                 val currentTime = System.currentTimeMillis()
                                 val testNoteObject = NoteModel(
                                     id = 10,
                                     title = "This is a test title",
                                     content = "This is a test content",
                                     lastEditTime = currentTime
                                 )

                                 coEvery { noteDao.getAllNotes() } returns flowOf(listOf(testNoteObject))
                                 val result = repository.getAllNotes().first()

                                 coVerify(exactly = 1) { noteDao.getAllNotes() }
                                 assert(result == listOf(testNoteObject))
                             }

                             @Test
                             fun `testing getSingleNote`() = runBlocking {
                                 val currentTime = System.currentTimeMillis()
                                 val testNoteObject = NoteModel(
                                     id = 10,
                                     title = "This is a test title",
                                     content = "This is a test content",
                                     lastEditTime = currentTime
                                 )
                                 coEvery { noteDao.getSingleNote(any()) } returns flowOf(testNoteObject)

                                 val result = repository.getSingleNote(100).first()

                                 coVerify(exactly = 1) { noteDao.getSingleNote(any()) }
                                 assert(result == testNoteObject)
                             }

                             @Test
                             fun `testing addOrUpdateNote`() = runBlocking {
                                 coEvery { noteDao.addOrUpdateNote(any()) } returns 100L

                                 val result = repository.addOrUpdateNote(
                                     NoteModel(
                                         id = 10,
                                         title = "This is a test title",
                                         content = "This is a test content",
                                         lastEditTime = System.currentTimeMillis()
                                     )
                                 )

                                 coVerify(exactly = 1) { noteDao.addOrUpdateNote(any()) }
                                 assert(result == 100L)
                             }

                             @Test
                             fun `testing deleteNote`() = runBlocking {
                                 coEvery { noteDao.deleteNote(any()) } returns 1
                                 repository.deleteNote(200)
                                 coVerify(exactly = 1) { noteDao.deleteNote(any()) }
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class AddNoteUseCaseTest {

                             private lateinit var useCase: AddNoteUseCase
                             private lateinit var repository: NoteRepository

                             @Before
                             fun setUp() {
                                 repository = mockk()
                                 useCase = AddNoteUseCase(repository)
                             }

                             @Test
                             fun testing_WhenNoteTitleIsEmpty() = runBlocking {
                                 val testNoteId = 5L
                                 val testNote = Note(
                                     id = testNoteId,
                                     title = "",
                                     content = "test content",
                                     lastEditTime = System.currentTimeMillis()
                                 )

                                 val addNoteExceptionFound = try {
                                     useCase.execute(testNote)
                                     false
                                 } catch (e: AddNoteException) {
                                     TestCase.assertEquals(e.reason, R.string.error_empty_title)
                                     true
                                 } catch (e: Exception) {
                                     false
                                 }
                                 TestCase.assertEquals(true, addNoteExceptionFound)
                             }

                             @Test
                             fun testing_WhenNoteContentIsEmpty() = runBlocking {
                                 val testNoteId = 5L
                                 val testNote = Note(
                                     id = testNoteId,
                                     title = "test title",
                                     content = "",
                                     lastEditTime = System.currentTimeMillis()
                                 )

                                 val addNoteExceptionFound = try {
                                     useCase.execute(testNote)
                                     false
                                 } catch (e: AddNoteException) {
                                     TestCase.assertEquals(e.reason, R.string.error_empty_content)
                                     true
                                 } catch (e: Exception) {
                                     false
                                 }
                                 TestCase.assertEquals(true, addNoteExceptionFound)
                             }

                             @Test
                             fun testing_WhenDatabaseInternalError() = runBlocking {
                                 coEvery { repository.addOrUpdateNote(any()) } returns -1

                                 val testNoteId = 5L
                                 val testNote = Note(
                                     id = testNoteId,
                                     title = "test title",
                                     content = "test content",
                                     lastEditTime = System.currentTimeMillis()
                                 )

                                 val addNoteExceptionFound = try {
                                     useCase.execute(testNote)
                                     false
                                 } catch (e: AddNoteException) {
                                     TestCase.assertEquals(e.reason, R.string.error_unknown_exception)
                                     true
                                 } catch (e: Exception) {
                                     false
                                 }
                                 TestCase.assertEquals(true, addNoteExceptionFound)
                             }

                             @Test
                             fun testing_SuccessfulCase() = runBlocking {
                                 val slot = CapturingSlot<NoteModel>()
                                 coEvery { repository.addOrUpdateNote(capture(slot)) } answers { slot.captured.id }

                                 val testNoteId = 5L
                                 val testNote = Note(
                                     id = testNoteId,
                                     title = "test title",
                                     content = "test content",
                                     lastEditTime = System.currentTimeMillis()
                                 )

                                 val anyExceptionFound = try {
                                     useCase.execute(testNote)
                                     false
                                 } catch (e: Exception) {
                                     true
                                 }
                                 TestCase.assertEquals(false, anyExceptionFound)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class DeleteNoteUseCaseTest {

                             private lateinit var repository: NoteRepository
                             private lateinit var useCase: DeleteNoteUseCase

                             @Before
                             fun setup() {
                                 repository = mockk()
                                 useCase = DeleteNoteUseCase(repository)
                             }

                             @Test
                             fun tryingToDeleteAnUnknownItem() = runBlocking {
                                 val testNoteId = 5L
                                 coEvery { repository.deleteNote(testNoteId) } returns 0

                                 val isDeleteExceptionFound = try {
                                     useCase.execute(testNoteId)
                                     false
                                 } catch (e: DeleteNoteException) {
                                     TestCase.assertEquals(e.reason, R.string.error_unknown_exception)
                                     true
                                 } catch (e: Exception) {
                                     false
                                 }
                                 TestCase.assertEquals(true, isDeleteExceptionFound)
                             }

                             @Test
                             fun tryingToDeleteAValidItem() = runBlocking {
                                 val testNoteId = 5L
                                 coEvery { repository.deleteNote(testNoteId) } returns 1

                                 val isExceptionFound = try {
                                     useCase.execute(testNoteId)
                                     false
                                 } catch (e: Exception) {
                                     true
                                 }
                                 TestCase.assertEquals(false, isExceptionFound)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class GetNotesUseCaseTest {

                             private lateinit var repository: NoteRepository
                             private lateinit var useCase: GetNotesUseCase

                             @Before
                             fun setup() {
                                 repository = mockk()
                                 useCase = GetNotesUseCase(repository)
                             }

                             @Test
                             fun testingGetNoteApiWhenListIsEmpty() = runBlocking {
                                 coEvery { repository.getAllNotes() } returns flowOf(emptyList())
                                 val noteList = useCase.execute().first()
                                 TestCase.assertEquals(0, noteList.size)
                             }

                             @Test
                             fun testingGetNoteApiWhenListIsNotEmpty() = runBlocking {
                                 val testNoteModelList = listOf(
                                     NoteModel(
                                         id = 1L,
                                         title = "First note title",
                                         content = "First note content",
                                         lastEditTime = 101L
                                     ), NoteModel(
                                         id = 2L,
                                         title = "Second note title",
                                         content = "Second note content",
                                         lastEditTime = 102L
                                     )
                                 )
                                 coEvery { repository.getAllNotes() } returns flowOf(testNoteModelList)
                                 val noteList = useCase.execute().first()
                                 TestCase.assertEquals(2, noteList.size)

                                 // the list is sorted according to last edit time so the note whose id
                                 // is 2 will be the first item since it's edited last
                                 TestCase.assertEquals(2L, noteList[0].id)
                                 TestCase.assertEquals("Second note title", noteList[0].title)
                                 TestCase.assertEquals(1L, noteList[1].id)
                                 TestCase.assertEquals("First note title", noteList[1].title)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class GetSingleNoteUseCaseTest {

                             private lateinit var repository: NoteRepository
                             private lateinit var useCase: GetSingleNoteUseCase

                             @Before
                             fun setup() {
                                 repository = mockk()
                                 useCase = GetSingleNoteUseCase(repository)
                             }

                             @Test
                             fun getSingleNoteWhenTheListIsEmpty() = runBlocking {
                                 val testNoteId = 5L
                                 coEvery { repository.getSingleNote(testNoteId) } returns flowOf(null)

                                 val singleNote = useCase.execute(testNoteId).first()
                                 TestCase.assertEquals(null, singleNote)
                             }

                             @Test
                             fun getSingleNoteWhenTheListIsNotEmpty() = runBlocking {
                                 val testNoteId = 5L
                                 val testNote = NoteModel(
                                     id = testNoteId,
                                     title = "test title",
                                     content = "test content",
                                     lastEditTime = 100L
                                 )
                                 coEvery { repository.getSingleNote(testNoteId) } returns flowOf(testNote)

                                 val singleNote = useCase.execute(testNoteId).first()
                                 TestCase.assertEquals(testNote.id, singleNote?.id)
                                 TestCase.assertEquals(testNote.title, singleNote?.title)
                                 TestCase.assertEquals(testNote.content, singleNote?.content)
                                 TestCase.assertEquals(testNote.lastEditTime, singleNote?.lastEditTime)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @OptIn(ExperimentalCoroutinesApi::class)
                        class AddEditNoteViewModelTest {

                            private lateinit var viewModel: AddEditNoteViewModel
                            private lateinit var addNoteUseCase: AddNoteUseCase
                            private lateinit var getSingleNoteUseCase: GetSingleNoteUseCase
                            private lateinit var savedStateHandle: SavedStateHandle

                            private val testDispatcher = StandardTestDispatcher()

                            @Before
                            fun setUp() {
                                addNoteUseCase = mockk()
                                getSingleNoteUseCase = mockk()
                                savedStateHandle = mockk()

                                Dispatchers.setMain(testDispatcher)
                            }

                            @After
                            fun tearDown() {
                                Dispatchers.resetMain()
                            }

                            @Test
                            fun instantiationTestWhenAddingNewNote() = runTest {
                                every { savedStateHandle.get<Long>("noteId") } returns -1L
                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                TestCase.assertEquals(false, viewModel.isEditingNote.first())
                                TestCase.assertEquals("", viewModel.titleText.first())
                                TestCase.assertEquals("", viewModel.contentText.first())
                            }

                            @Test
                            fun instantiationTestWhenEditingPreviousNote() = runTest {
                                val testNote = Note(
                                    id = 5L,
                                    title = "title",
                                    content = "content",
                                    lastEditTime = 100L
                                )
                                every { savedStateHandle.get<Long>("noteId") } returns testNote.id
                                every { getSingleNoteUseCase.execute(any()) } returns flowOf(testNote)

                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                TestCase.assertEquals(true, viewModel.isEditingNote.first())
                                TestCase.assertEquals("title", viewModel.titleText.first())
                                TestCase.assertEquals("content", viewModel.contentText.first())
                            }

                            @Test
                            fun testingByChangingTitleText() = runTest {
                                every { savedStateHandle.get<Long>("noteId") } returns -1L
                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.UpdateTitle("nice title"))
                                testDispatcher.scheduler.advanceUntilIdle()

                                TestCase.assertEquals(false, viewModel.isEditingNote.first())
                                TestCase.assertEquals("nice title", viewModel.titleText.first())
                                TestCase.assertEquals("", viewModel.contentText.first())
                            }

                            @Test
                            fun testingByChangingContentText() = runTest {
                                every { savedStateHandle.get<Long>("noteId") } returns -1L
                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.UpdateContent("nice content"))
                                testDispatcher.scheduler.advanceUntilIdle()

                                TestCase.assertEquals(false, viewModel.isEditingNote.first())
                                TestCase.assertEquals("", viewModel.titleText.first())
                                TestCase.assertEquals("nice content", viewModel.contentText.first())
                            }

                            @Test
                            fun testingByTryingToSaveInvalidNote() = runTest {
                                every { savedStateHandle.get<Long>("noteId") } returns -1L
                                coEvery { addNoteUseCase.execute(any()) } throws AddNoteException(1)

                                var toastMessageId = -1
                                val toastCollectionJob = launch {
                                    viewModel.toastMessage.collect { messageId ->
                                        messageId?.let { toastMessageId = it }
                                    }
                                }

                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.SaveNote)
                                testDispatcher.scheduler.advanceUntilIdle()

                                // we are canceling the job other wise the test won't be completed
                                // cause a sub coroutine will always be running to catch the updated
                                // value of the toast message shared flow.
                                toastCollectionJob.cancel()

                                TestCase.assertEquals(1, toastMessageId)
                            }

                            @Test
                            fun testingByTryingToSaveValidNote() = runTest {
                                every { savedStateHandle.get<Long>("noteId") } returns -1L
                                coEvery { addNoteUseCase.execute(any()) } returns Unit

                                var shouldCloseScreenValue = false
                                val screenCloseValueCollectorJob = launch {
                                    viewModel.shouldCloseScreen.collect { shouldClose ->
                                        shouldClose?.let { shouldCloseScreenValue = it }
                                    }
                                }

                                viewModel = AddEditNoteViewModel(getSingleNoteUseCase, addNoteUseCase, savedStateHandle)
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.UpdateTitle("nice title"))
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.UpdateContent("nice content"))
                                testDispatcher.scheduler.advanceUntilIdle()

                                viewModel.handleEvent(AddEditUiEvent.SaveNote)
                                testDispatcher.scheduler.advanceUntilIdle()

                                // we are canceling the job other wise the test won't be completed
                                // cause a sub coroutine will always be running to catch the updated
                                // value of the toast message shared flow.
                                screenCloseValueCollectorJob.cancel()

                                TestCase.assertEquals(true, shouldCloseScreenValue)
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @OptIn(ExperimentalCoroutinesApi::class)
                         class NoteListViewModelTest {

                             private lateinit var viewModel: NoteListViewModel
                             private lateinit var getNotesUseCase: GetNotesUseCase

                             private val testDispatcher = StandardTestDispatcher()

                             @Before
                             fun setUp() {
                                 getNotesUseCase = mockk()
                                 Dispatchers.setMain(testDispatcher)
                             }

                             @After
                             fun tearDown() {
                                 Dispatchers.resetMain()
                             }

                             @Test
                             fun testingWhenTheListIsEmpty() = runTest {
                                 every { getNotesUseCase.execute() } returns flowOf(emptyList())
                                 viewModel = NoteListViewModel(getNotesUseCase)
                                 testDispatcher.scheduler.advanceUntilIdle()
                                 val resultNoteList = viewModel.noteList.first()
                                 TestCase.assertEquals(0, resultNoteList?.size)
                             }

                             @Test
                             fun testingWhenTheListIsNotEmpty() = runTest {
                                 val testNoteList = listOf(
                                     NoteAsListItem(
                                         id = 1L,
                                         title = "first"
                                     ), NoteAsListItem(
                                         id = 2L,
                                         title = "second"
                                     )
                                 )
                                 every { getNotesUseCase.execute() } returns flowOf(testNoteList)

                                 viewModel = NoteListViewModel(getNotesUseCase)
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 val resultNoteList = viewModel.noteList.first()
                                 TestCase.assertEquals(2, resultNoteList?.size)
                                 TestCase.assertEquals(1L, resultNoteList?.get(0)?.id)
                                 TestCase.assertEquals("first", resultNoteList?.get(0)?.title)
                                 TestCase.assertEquals(2L, resultNoteList?.get(1)?.id)
                                 TestCase.assertEquals("second", resultNoteList?.get(1)?.title)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         @OptIn(ExperimentalCoroutinesApi::class)
                         class ShowNoteViewModelTest {

                             private lateinit var viewModel: ShowNoteViewModel
                             private lateinit var deleteNoteUseCase: DeleteNoteUseCase
                             private lateinit var getSingleNoteUseCase: GetSingleNoteUseCase
                             private lateinit var savedStateHandle: SavedStateHandle

                             private val testDispatcher = StandardTestDispatcher()

                             @Before
                             fun setUp() {
                                 deleteNoteUseCase = mockk()
                                 getSingleNoteUseCase = mockk()
                                 savedStateHandle = mockk()

                                 Dispatchers.setMain(testDispatcher)
                             }

                             @After
                             fun tearDown() {
                                 Dispatchers.resetMain()
                             }

                             @Test
                             fun testingByInitiatingRelativeNote() = runTest {
                                 val testNote = Note(
                                     id = 1L,
                                     title = "note title",
                                     content = "note content",
                                     lastEditTime = 100L
                                 )
                                 every { savedStateHandle.get<Long>("noteId") } returns testNote.id
                                 coEvery { getSingleNoteUseCase.execute(any()) } returns flowOf(testNote)

                                 viewModel = ShowNoteViewModel(getSingleNoteUseCase, deleteNoteUseCase, savedStateHandle)
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 TestCase.assertEquals(testNote, viewModel.currentNote.first())
                             }

                             @Test
                             fun testingByTryingToDeleteNoteFailedCase() = runTest {
                                 val testNote = Note(
                                     id = 1L,
                                     title = "note title",
                                     content = "note content",
                                     lastEditTime = 100L
                                 )
                                 every { savedStateHandle.get<Long>("noteId") } returns testNote.id
                                 coEvery { getSingleNoteUseCase.execute(any()) } returns flowOf(testNote)
                                 coEvery { deleteNoteUseCase.execute(any()) } throws DeleteNoteException(1)

                                 viewModel = ShowNoteViewModel(getSingleNoteUseCase, deleteNoteUseCase, savedStateHandle)
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 var toastMessageId = -1
                                 val toastCollectionJob = launch {
                                     viewModel.toastMessage.collect { messageId ->
                                         messageId?.let { toastMessageId = it }
                                     }
                                 }

                                 viewModel.handleEvent(ShowNoteUiEvent.DeleteNote(testNote.id))
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 toastCollectionJob.cancel()

                                 TestCase.assertEquals(1, toastMessageId)
                             }

                             @Test
                             fun testingByTryingToDeleteNoteSuccessfulCase() = runTest {
                                 val testNote = Note(
                                     id = 1L,
                                     title = "note title",
                                     content = "note content",
                                     lastEditTime = 100L
                                 )
                                 every { savedStateHandle.get<Long>("noteId") } returns testNote.id
                                 coEvery { getSingleNoteUseCase.execute(any()) } returns flowOf(testNote)
                                 coEvery { deleteNoteUseCase.execute(any()) } returns Unit

                                 viewModel = ShowNoteViewModel(getSingleNoteUseCase, deleteNoteUseCase, savedStateHandle)
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 var shouldCloseScreenValue = false
                                 val screenCloseValueCollectorJob = launch {
                                     viewModel.shouldCloseScreen.collect { shouldClose ->
                                         shouldClose?.let { shouldCloseScreenValue = it }
                                     }
                                 }

                                 viewModel.handleEvent(ShowNoteUiEvent.DeleteNote(testNote.id))
                                 testDispatcher.scheduler.advanceUntilIdle()

                                 screenCloseValueCollectorJob.cancel()

                                 TestCase.assertEquals(true, shouldCloseScreenValue)
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         class ExampleUnitTest {
                             @Test
                             fun addition_isCorrect() {
                                 assertEquals(4, 2 + 2)
                             }
                         }
                    """.trimIndent()
            )

            //https://github.com/AndreDLeite/KCMPPlayground (Desktop Room Database Setup)
            KotlinCode(
                code = """
                    import androidx.room.Room
                    import androidx.room.RoomDatabase
                    import java.io.File

                    actual class DatabaseFactory {
                        actual fun create(): RoomDatabase.Builder<FavoriteProductDatabase> {
                            val os = System.getProperty("os.name").lowercase()
                            val userHome = System.getProperty("user.home")
                            val appDataDir = when {
                                os.contains("win") -> File(System.getenv("APPDATA"), "ECommerce")
                                os.contains("mac") -> File(userHome, "Library/Application Support/ECommerce")
                                else -> File(userHome, ".local/share/ECommerce")
                            }

                            if(!appDataDir.exists()) {
                                appDataDir.mkdirs()
                            }

                            val dbFile = File(appDataDir, FavoriteProductDatabase.DATABASE_NAME)
                            return Room.databaseBuilder(dbFile.absolutePath)
                        }
                    }
                    
                    @Database(
                        entities = [ProductEntity::class],
                        version = 1,
                    )

                    @ConstructedBy(ProductDatabaseConstructor::class)
                    abstract class FavoriteProductDatabase: RoomDatabase() {
                        abstract val favoritesProductDao: FavoriteProductDao

                        companion object {
                            const val DATABASE_NAME = "products.db"
                        }
                    }
                    
                    //gradle
                    compose.desktop {
                        application {
                            mainClass = "adl.appture.kcmp.MainKt"

                            nativeDistributions {
                                targetFormats(TargetFormat.Dmg, TargetFormat.Msi, TargetFormat.Deb)
                                packageName = "com.adl.appture"
                                packageVersion = "1.0.0"
                            }
                        }
                    }
                    """.trimIndent()
            )
            //④

            /*KotlinCode(
                code = """

                    """.trimIndent()
            )*/
        }
    }
}

/*
Have you considered the @𝐔𝐩𝐬𝐞𝐫𝐭 annotation in the Room Database?
This powerful feature streamlines your database operations, making your code cleaner and more efficient. An upsert method allows you to handle both inserting new data and updating existing data in one operation.

Here’s how it works:
𝐈𝐧𝐬𝐞𝐫𝐭: If the entity doesn't exist in the database (determined by the primary key), Room inserts the new data.
𝐔𝐩𝐝𝐚𝐭𝐞: If the entity already exists, Room updates the existing record with the new data.

https://github.com/vishal2376/bookie
https://github.com/jeromelarose/MonAlerte.git
*/